import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Link } from "@tanstack/react-router";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { RegisterInput } from "../../../../api/register";
import useFormValidity from "../stepUtils";
import RegisterFullNameTextField from "../../../../components/RegisterFullNameTextField";

export interface RegisterDataStepProps {
  handleNext: () => void;
  handleBack: () => void;
  handleCancel: () => void;
}

function RegisterDataStep({
  handleBack,
  handleCancel,
  handleNext,
}: RegisterDataStepProps): React.JSX.Element {
  const { t } = useTranslation();
  const methods = useFormContext<RegisterInput>();
  const { control } = methods;
  const isFormsValid = useFormValidity("name");

  return (
    <Stack alignItems="center" spacing={3}>
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState: { error } }) => {
          const matches = (() => {
            const result =
              error && error.types ? (error.types.matches as string[]) : [];
            if (!Array.isArray(result)) return [result];
            return result;
          })();

          return (
            <RegisterFullNameTextField
              {...field}
              color="primary"
              error={Boolean(error)}
              fullWidth
              label={t("register.fullNameLabel")}
              minFirstNameError={matches.some(
                (item) => item === "minFirstNameError",
              )}
              minThereNameError={matches.some(
                (item) => item === "minThereNameError",
              )}
              minTwoNameError={matches.some(
                (item) => item === "minTwoNameError",
              )}
              placeholder={t("register.fullNamePlaceholder")}
              sx={{
                "& .MuiFormLabel-root": {
                  color: "primary.main",
                },
                "& .MuiInputBase-input": {
                  color: "primary.main",
                },
              }}
              variant="outlined"
            />
          );
        }}
      />
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        marginTop="5%"
        width="100%"
      >
        <Button
          color="secondary"
          onClick={handleBack}
          size="large"
          tabIndex={-1}
          type="button"
          variant="outlined"
        >
          {t("common.previous")}
        </Button>
        <Button
          color="primary"
          disabled={!isFormsValid}
          onClick={handleNext}
          size="large"
          tabIndex={0}
          type="button"
          variant="contained"
        >
          {t("common.next")}
        </Button>
      </Box>
      <Box marginTop="10%" textAlign="end" width="100%">
        <Button
          color="success"
          component={Link}
          onClick={handleCancel}
          size="medium"
          to=""
          variant="text"
        >
          {t("common.cancelRegistration")}
        </Button>
      </Box>
    </Stack>
  );
}

export default RegisterDataStep;
